import React, { useEffect, useMemo, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import { minutesToHoursText } from './trainingReportForm';
import {
  buildAttendancePayload,
  buildParticipantPayload,
  countPendingAttendance,
  createAttendanceDraft,
  formatAttendanceDate,
  getAttendanceDates,
  getChangedReconciledHours,
  reconciledHoursToMinutes,
} from './trainingReportAttendance';
import {
  ATTENDANCE_SESSIONS_PER_PAGE,
  MAX_TRAINING_SESSIONS,
} from './trainingReportPolicy';
import './TrainingReportAttendance.css';

export const TrainingReportAttendanceStep = ({
  idReporte,
  accessToken,
  report,
  setReport,
  onPrevious,
}) => {
  const [rows, setRows] = useState(() => createAttendanceDraft(report));
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [needsRegeneration, setNeedsRegeneration] = useState(false);
  const [sessionPage, setSessionPage] = useState(1);

  const dates = useMemo(() => getAttendanceDates(report), [report]);
  const pendingCells = useMemo(
    () => countPendingAttendance(rows, dates),
    [rows, dates],
  );
  const totalSessionPages = Math.max(
    1,
    Math.ceil(dates.length / ATTENDANCE_SESSIONS_PER_PAGE),
  );
  const visibleDates = useMemo(() => {
    const start = (sessionPage - 1) * ATTENDANCE_SESSIONS_PER_PAGE;
    return dates.slice(start, start + ATTENDANCE_SESSIONS_PER_PAGE);
  }, [dates, sessionPage]);
  const exceedsSessionLimit = dates.length > MAX_TRAINING_SESSIONS;

  useEffect(() => {
    setRows(createAttendanceDraft(report));
  }, [report]);

  useEffect(() => {
    setSessionPage((current) => Math.min(current, totalSessionPages));
  }, [totalSessionPages]);

  const replaceRow = (participantId, updater) => {
    setRows((current) =>
      current.map((row) =>
        row.id_reporte_participante === participantId ? updater(row) : row,
      ),
    );
    setStatus(null);
  };

  const setGender = (participantId, genero) => {
    replaceRow(participantId, (row) => ({ ...row, genero }));
  };

  const setAttendance = (participantId, fecha, value) => {
    const normalized = value === '' ? null : value === 'si';
    replaceRow(participantId, (row) => ({
      ...row,
      asistencias: { ...row.asistencias, [fecha]: normalized },
    }));
  };

  const markAllYes = (fecha = null) => {
    setRows((current) =>
      current.map((row) => {
        const nextAttendance = { ...row.asistencias };
        (fecha ? [fecha] : dates).forEach((date) => {
          nextAttendance[date] = true;
        });
        return { ...row, asistencias: nextAttendance };
      }),
    );
    setStatus(null);
  };

  const setReconciledHours = (participantId, value) => {
    replaceRow(participantId, (row) => ({
      ...row,
      horas_conciliadas_horas: value,
    }));
  };

  const generateAttendance = async (regenerate = false) => {
    if (!accessToken || generating || saving) return;

    if (regenerate) {
      const confirmed = window.confirm(
        'La regeneración reemplazará la cuadrícula actual de asistencia y limpiará horas conciliadas. ¿Deseas continuar?',
      );
      if (!confirmed) return;
    }

    setGenerating(true);
    setStatus({
      type: 'info',
      message: regenerate
        ? 'Regenerando asistencias...'
        : 'Generando fechas de asistencia...',
    });

    try {
      const updated = await trainingReportApi.generateAttendances(
        idReporte,
        regenerate,
        accessToken,
      );
      setReport(updated);
      setNeedsRegeneration(false);
      setSessionPage(1);
      setStatus({
        type: 'success',
        message: `Asistencias listas: ${updated.sesiones_calculadas ?? getAttendanceDates(updated).length} sesión(es).`,
      });
    } catch (error) {
      console.error('No se pudieron generar las asistencias:', error);
      if (error?.code === 'ATTENDANCE_GRID_CONFLICT') {
        setNeedsRegeneration(true);
        setStatus({
          type: 'warning',
          message:
            'Las fechas u horarios cambiaron respecto de la cuadrícula existente. Confirma la regeneración para reemplazarla.',
        });
      } else {
        setStatus({
          type: 'error',
          message: error?.message || 'No fue posible generar las asistencias.',
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  const validateHours = () => {
    const invalid = rows.find(
      (row) =>
        reconciledHoursToMinutes(row.horas_conciliadas_horas) === undefined,
    );
    if (!invalid) return true;

    setStatus({
      type: 'error',
      message: `Revisa las horas conciliadas de ${invalid.nombre_completo}. Deben ser cero o mayores.`,
    });
    return false;
  };

  const saveStep = async () => {
    if (!accessToken || saving || generating || !validateHours()) return;

    if (exceedsSessionLimit) {
      setStatus({
        type: 'error',
        message: `Este reporte tiene ${dates.length} sesiones y el máximo permitido es ${MAX_TRAINING_SESSIONS}. Regresa a Datos generales y reduce el rango o los días seleccionados.`,
      });
      return;
    }

    setSaving(true);
    setStatus({ type: 'info', message: 'Guardando participantes y asistencia...' });

    try {
      await trainingReportApi.updateParticipants(
        idReporte,
        buildParticipantPayload(rows),
        accessToken,
      );

      if (dates.length > 0) {
        await trainingReportApi.updateAttendances(
          idReporte,
          buildAttendancePayload(rows, dates),
          accessToken,
        );
      }

      const changedHours = getChangedReconciledHours(rows, report);
      await Promise.all(
        changedHours.map((item) =>
          trainingReportApi.updateReconciledHours(
            idReporte,
            item.id_reporte_participante,
            item.horas_conciliadas_minutos,
            accessToken,
          ),
        ),
      );

      const refreshed = await trainingReportApi.get(idReporte, accessToken);
      setReport(refreshed);
      setStatus({
        type: 'success',
        message:
          dates.length > 0
            ? 'Participantes y asistencia guardados correctamente.'
            : 'Participantes guardados. Genera la cuadrícula para capturar asistencias.',
      });
    } catch (error) {
      console.error('No se pudo guardar participantes y asistencia:', error);
      setStatus({
        type: 'error',
        message:
          error?.message || 'No fue posible guardar participantes y asistencia.',
      });
    } finally {
      setSaving(false);
    }
  };

  const statusClass =
    status?.type === 'success'
      ? 'admin-operation-status-success'
      : status?.type === 'error'
        ? 'admin-operation-status-error'
        : 'admin-operation-status-warning';

  const firstVisibleSession =
    dates.length === 0
      ? 0
      : (sessionPage - 1) * ATTENDANCE_SESSIONS_PER_PAGE + 1;
  const lastVisibleSession = Math.min(
    sessionPage * ATTENDANCE_SESSIONS_PER_PAGE,
    dates.length,
  );

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 2 de 7</p>
          <h2>Participantes y asistencia</h2>
          <p>
            Captura el género, registra SI/NO por cada sesión y revisa las horas
            calculadas. Las horas conciliadas son un ajuste manual opcional.
          </p>
        </div>
        <span className={`training-report-status training-report-status-${report?.estado || 'borrador'}`}>
          {report?.estado === 'generado' ? 'Generado' : 'Borrador'}
        </span>
      </div>

      <div className="training-report-attendance-toolbar">
        <div className="training-report-summary-grid training-report-attendance-summary">
          <div>
            <span>Participantes</span>
            <strong>{rows.length}</strong>
          </div>
          <div>
            <span>Sesiones</span>
            <strong>{dates.length}</strong>
          </div>
          <div>
            <span>Celdas pendientes</span>
            <strong>{pendingCells}</strong>
          </div>
        </div>

        <div className="training-report-attendance-actions">
          <button
            className="admin-operation-button admin-operation-button-secondary"
            type="button"
            onClick={() => generateAttendance(false)}
            disabled={generating || saving}
          >
            {generating ? 'Procesando...' : dates.length ? 'Verificar fechas' : 'Generar asistencias'}
          </button>
          {(needsRegeneration || dates.length > 0) && (
            <button
              className="admin-operation-button admin-operation-button-secondary"
              type="button"
              onClick={() => generateAttendance(true)}
              disabled={generating || saving}
            >
              Regenerar asistencias
            </button>
          )}
          {dates.length > 0 && !exceedsSessionLimit && (
            <button
              className="admin-operation-button admin-operation-button-secondary"
              type="button"
              onClick={() => markAllYes()}
              disabled={generating || saving}
            >
              Marcar todos SI
            </button>
          )}
        </div>
      </div>

      {status && (
        <div className={`admin-operation-status ${statusClass}`} role="status">
          {status.message}
        </div>
      )}

      {exceedsSessionLimit && (
        <div className="admin-operation-status admin-operation-status-error training-report-inline-status" role="alert">
          Este borrador tiene {dates.length} sesiones. El nuevo límite es de{' '}
          {MAX_TRAINING_SESSIONS}. Regresa a Datos generales, ajusta el rango o los
          días y después regenera las asistencias.
        </div>
      )}

      {dates.length === 0 && (
        <div className="admin-operation-status admin-operation-status-warning training-report-inline-status">
          Aún no hay fechas de asistencia. Guarda primero los datos generales y
          después usa “Generar asistencias”.
        </div>
      )}

      {dates.length > 0 && (
        <div className="training-report-session-pagination">
          <button
            className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
            type="button"
            disabled={sessionPage === 1 || saving || generating}
            onClick={() => setSessionPage((current) => current - 1)}
          >
            Fechas anteriores
          </button>
          <span>
            Sesiones {firstVisibleSession}–{lastVisibleSession} de {dates.length}
          </span>
          <button
            className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
            type="button"
            disabled={sessionPage === totalSessionPages || saving || generating}
            onClick={() => setSessionPage((current) => current + 1)}
          >
            Fechas siguientes
          </button>
        </div>
      )}

      <div className="admin-operation-table-wrapper training-report-attendance-table-wrapper">
        <table className="admin-operation-table training-report-attendance-table">
          <colgroup>
            <col className="training-report-width-number" />
            <col className="training-report-width-participant" />
            <col className="training-report-width-gender" />
            {visibleDates.map((date) => (
              <col className="training-report-width-session" key={`column-${date}`} />
            ))}
            <col className="training-report-width-hours" />
            <col className="training-report-width-hours" />
            <col className="training-report-width-percent" />
          </colgroup>
          <thead>
            <tr>
              <th className="training-report-sticky-col training-report-col-number">No.</th>
              <th className="training-report-sticky-col training-report-col-name">Participante</th>
              <th className="training-report-col-gender">Género</th>
              {visibleDates.map((date) => (
                <th className="training-report-session-header" key={date}>
                  <span>{formatAttendanceDate(date)}</span>
                  <button
                    type="button"
                    onClick={() => markAllYes(date)}
                    disabled={saving || generating || exceedsSessionLimit}
                  >
                    Todos SI
                  </button>
                </th>
              ))}
              <th className="training-report-summary-column">Hrs.<br />calculadas</th>
              <th className="training-report-summary-column">Hrs.<br />conciliadas</th>
              <th className="training-report-summary-column">%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id_reporte_participante}>
                <td className="training-report-sticky-col training-report-col-number">{index + 1}</td>
                <td className="training-report-sticky-col training-report-col-name">
                  <strong>{row.nombre_completo}</strong>
                  <span className="training-report-participant-meta">
                    Exp. labor.: {row.matricula_alumno || 'Pendiente'}
                  </span>
                </td>
                <td className="training-report-col-gender">
                  <select
                    className="training-report-compact-select"
                    value={row.genero}
                    onChange={(event) =>
                      setGender(row.id_reporte_participante, event.target.value)
                    }
                    disabled={saving || generating || exceedsSessionLimit}
                    aria-label={`Género de ${row.nombre_completo}`}
                  >
                    <option value="">Pendiente</option>
                    <option value="H">H</option>
                    <option value="M">M</option>
                  </select>
                </td>
                {visibleDates.map((date) => {
                  const value = row.asistencias?.[date];
                  return (
                    <td className="training-report-session-cell" key={date}>
                      <select
                        className={`training-report-attendance-select ${value === true ? 'training-report-attendance-yes' : value === false ? 'training-report-attendance-no' : ''}`}
                        value={value === true ? 'si' : value === false ? 'no' : ''}
                        onChange={(event) =>
                          setAttendance(
                            row.id_reporte_participante,
                            date,
                            event.target.value,
                          )
                        }
                        disabled={saving || generating || exceedsSessionLimit}
                        aria-label={`Asistencia de ${row.nombre_completo} el ${formatAttendanceDate(date)}`}
                      >
                        <option value="">—</option>
                        <option value="si">SI</option>
                        <option value="no">NO</option>
                      </select>
                    </td>
                  );
                })}
                <td className="training-report-summary-cell">
                  {minutesToHoursText(row.horas_calculadas_minutos) || '0'} h
                </td>
                <td className="training-report-summary-cell">
                  <input
                    className="training-report-hours-input"
                    type="number"
                    min="0"
                    step="0.25"
                    value={row.horas_conciliadas_horas}
                    onChange={(event) =>
                      setReconciledHours(
                        row.id_reporte_participante,
                        event.target.value,
                      )
                    }
                    placeholder="Auto"
                    disabled={saving || generating || exceedsSessionLimit}
                    aria-label={`Horas conciliadas de ${row.nombre_completo}`}
                  />
                </td>
                <td className="training-report-summary-cell training-report-percent-cell">
                  {row.porcentaje_asistencia === null
                    ? 'Pendiente'
                    : `${Number(row.porcentaje_asistencia).toFixed(2).replace(/\.00$/, '')}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="training-report-table-note">
        Se muestran hasta {ATTENDANCE_SESSIONS_PER_PAGE} fechas a la vez para mantener
        la captura legible. “Exp. labor.” se muestra debajo del participante en esta
        pantalla para ahorrar espacio; en el PDF permanece como columna independiente.
        Antes de cerrar el PDF definitivo validaremos su equivalencia con la matrícula.
      </p>

      <div className="training-report-editor-actions">
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onPrevious}
          disabled={saving || generating}
        >
          Volver a datos generales
        </button>
        <button
          className="admin-operation-button"
          type="button"
          onClick={saveStep}
          disabled={saving || generating || exceedsSessionLimit}
        >
          {saving ? 'Guardando...' : 'Guardar participantes y asistencia'}
        </button>
      </div>
    </>
  );
};
